/**
 * Smart Document Reader (US-163 / F-180) — front-end surface.
 *
 * Enterprise-only (gate: src/gates/smart-reader). Uploaded documents are
 * handed to the LP document-processing pipeline; the FE slice renders the
 * extract summary/queue state and links to Evidence Hub. The OCR/Lambda
 * processing (T-163-003) runs server-side on buffy.
 */
import React, { useState } from 'react';
import { Link } from 'react-router-dom';

export const SmartDocumentReader: React.FC = () => {
    const [dragOver, setDragOver] = useState(false);
    const [queued, setQueued] = useState<string[]>([]);

    const handleFiles = (files: FileList | null) => {
        if (!files || files.length === 0) return;
        setQueued((prev) => [
            ...prev,
            ...Array.from(files).map((f) => f.name),
        ]);
    };

    return (
        <div className="max-w-7xl mx-auto px-4 py-8" data-testid="smart-document-reader">
            <div className="flex items-center justify-between mb-8">
                <div>
                    <h1 className="text-2xl font-bold text-gray-900 flex items-center">
                        <i className="fas fa-file-lines text-indigo-600 mr-3"></i>
                        Smart Document Reader
                    </h1>
                    <p className="text-sm text-gray-500 mt-1">
                        Extract key facts from leases, notices, and inspection docs — automatically.
                    </p>
                </div>
                <Link
                    to="/texas/evidence"
                    data-testid="smart-reader-evidence-link"
                    className="text-sm font-medium text-indigo-600 hover:text-indigo-500"
                >
                    View Evidence Hub <i className="fas fa-arrow-right ml-1"></i>
                </Link>
            </div>

            <div
                role="button"
                tabIndex={0}
                aria-label="Drag and drop documents to upload"
                onClick={() => document.getElementById('smart-reader-file-input')?.click()}
                onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                        document.getElementById('smart-reader-file-input')?.click();
                    }
                }}
                onDragOver={(e) => {
                    e.preventDefault();
                    setDragOver(true);
                }}
                onDragLeave={() => setDragOver(false)}
                onDrop={(e) => {
                    e.preventDefault();
                    setDragOver(false);
                    handleFiles(e.dataTransfer.files);
                }}
                data-testid="smart-reader-dropzone"
                className={`border-2 border-dashed rounded-2xl p-12 text-center transition-colors cursor-pointer ${
                    dragOver
                        ? 'border-indigo-500 bg-indigo-50'
                        : 'border-gray-300 bg-white hover:border-indigo-300'
                }`}
            >
                <i className="fas fa-cloud-arrow-up text-3xl text-gray-400 mb-3"></i>
                <p className="text-gray-700 font-medium">Drop documents here or click to browse</p>
                <p className="text-xs text-gray-400 mt-1">PDF, PNG, JPG — leases, notices, inspection records</p>
                <input
                    id="smart-reader-file-input"
                    type="file"
                    multiple
                    accept=".pdf,.png,.jpg,.jpeg"
                    className="hidden"
                    data-testid="smart-reader-file-input"
                    onChange={(e) => handleFiles(e.target.files)}
                />
            </div>

            <div className="mt-6" data-testid="smart-reader-queue">
                {queued.length === 0 ? (
                    <p className="text-sm text-gray-500 text-center py-4" data-testid="smart-reader-empty-state">
                        No documents queued yet.
                    </p>
                ) : (
                    <ul className="divide-y divide-gray-100 border border-gray-100 rounded-xl">
                        {queued.map((name) => (
                            <li key={name} className="px-4 py-3 flex items-center justify-between text-sm">
                                <span className="flex items-center text-gray-700">
                                    <i className="fas fa-file-pdf text-red-400 mr-3"></i>
                                    {name}
                                </span>
                                <span className="text-xs text-sky-600 flex items-center">
                                    <i className="fas fa-spinner fa-spin mr-1.5"></i>
                                    Processing
                                </span>
                            </li>
                        ))}
                    </ul>
                )}
            </div>
        </div>
    );
};

export default SmartDocumentReader;